/**
 * Copyright (c) 2010-2023 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { MapCommand } from '../map-command.class';
import { take } from 'rxjs/operators';
import { DisableMapTip, EnableMapTip } from '../../state/active-map.actions';
import { Select, Store } from '@ngxs/store';
import { ActiveMapState } from '../../state/active-map.state';
import { BehaviorSubject, Observable } from 'rxjs';
import { MapInteractionsService } from '../../services/mapInteractions.service';
import { LoggerService } from '../../../shared/services/logger.service';
import { MeasureDialogComponent } from './dialog/measure-dialog.component';
import { MeasureEvent } from '../../interaction/measure.interaction';
import { DialogRef } from '@angular/cdk/dialog';
import { MeasureDialog } from './measure-dialog.service';

export class MeasureCommand extends MapCommand {
    @Select(ActiveMapState.mapTipEnabled) private mapTipEnabled$: Observable<boolean>;
    private mapTipOriginalState: boolean;

    private store: Store;
    private interactions: MapInteractionsService;
    private dialog: MeasureDialog;
    private logger: LoggerService;

    private isMeasuring = false;

    private dialogRef: DialogRef<unknown, MeasureDialogComponent>;

    private measureData$ = new BehaviorSubject<MeasureEvent>(null);

    invoke() {
        if (!this.isMeasuring) {
            this.beginMeasure();
        } else {
            this.dialogRef.close();
        }
    }

    protected onCommandInit() {
        super.onCommandInit();
        this.store = this.deps['store'];
        this.interactions = this.deps['interactions'];
        this.dialog = this.deps['dialog'];
        this.logger = this.deps['logger'];
    }

    private beginMeasure() {
        this.mapTipEnabled$.pipe(take(1)).subscribe((isMaptipEnabled) => {
            this.isMeasuring = true;
            this.customLabel$.next('Clear Measurement');
            this.mapTipOriginalState = isMaptipEnabled;
            this.interactions.removeInteractions();
            this.store.dispatch(new DisableMapTip());
            this.interactions.enableMeasure({
                displayUnits: this.attributes['displayUnits'],
                onStart: (evt: MeasureEvent) => {
                    this.dialogRef = this.dialog.open(MeasureDialogComponent, {
                        width: '380px',
                        height: '120px',
                        ariaModal: false,
                        hasBackdrop: false,
                        disableClose: true,
                        panelClass: 'dash-gis-measure-dialog-container',
                        data: this.measureData$,
                    });
                    this.dialogRef.closed.pipe(take(1)).subscribe(() => this.cleanup());
                    this.measureData$.next(evt);
                },
                onChange: (evt: MeasureEvent) => {
                    this.measureData$.next(evt);
                },
                onEnd: (evt: MeasureEvent) => {
                },
            });
        });

        this.deactivated$.pipe(take(1)).subscribe(() => this.deactivated());
    }

    private deactivated() {
        if (this.isMeasuring) {
            this.interactions.enablePanInteraction();
            if (this.mapTipOriginalState) {
                this.store.dispatch(new EnableMapTip());
            }
            this.isMeasuring = false;
        }
    }

    private cleanup() {
        this.customLabel$.next(null);
        if (this.dialog.getDialogById(this.dialogRef.id)) {
            this.dialogRef.close();
        }
        this.interactions.clearMeasure();
        this.deactivate$.next();
    }
}
